export const en = {
  // Common
  common: {
    loading: "Loading...",
    error: "An error occurred",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    previous: "Previous",
    search: "Search",
    filter: "Filter",
    all: "All",
    none: "None",
    yes: "Yes",
    no: "No",
    or: "or",
  },

  // Auth
  auth: {
    login: "Login",
    register: "Sign up",
    logout: "Logout",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    fullName: "Full name",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    continueWithGoogle: "Continue with Google",
    loginSuccess: "Login successful",
    registerSuccess: "Registration successful",
    logoutSuccess: "Logout successful",
    invalidCredentials: "Invalid email or password",
    emailRequired: "Email is required",
    passwordRequired: "Password is required",
    passwordTooShort: "Password: at least 10 characters, with 2 of: digit, symbol, uppercase",
    emailAlreadyExists: "This email is already in use",
  },

  // Navigation
  nav: {
    home: "Home",
    dashboard: "Dashboard",
    interviews: "Interviews",
    resumes: "Resumes",
    profile: "Profile",
    settings: "Settings",
    admin: "Admin",
    groups: "Groups",
  },

  // Landing Page
  landing: {
    hero: {
      badge: "All-in-one career platform",
      title: "Your entire job search in one single ecosystem",
      subtitle:
        "One place for your resume, cover letters, interviews, LinkedIn and application tracking. Each tool builds on the others — one effort, results everywhere.",
      cta: "Create free account",
      secondaryCta: "See demo",
      reassurance: "Free · No credit card · Works in FR & EN",
      login: "Log in",
    },
    trustBar: {
      item1: "Built for the French market",
      item2: "Your data stays private",
      item3: "Powered by best-in-class AI",
    },
    problem: {
      title: "Finding a job shouldn't be a second job.",
      subtitle: "We know the struggle. Velyq makes it simple.",
      without: {
        title: "Without Velyq",
        items: [
          "Hours spent rewriting your resume for each role",
          "Tools scattered everywhere: ChatGPT, Canva, LinkedIn…",
          "Interviews blown due to lack of prep",
          "No idea of your ATS compatibility score",
        ],
      },
      with: {
        title: "With Velyq",
        items: [
          "Resume optimized in 30 seconds per job",
          "All your career tools in one place",
          "Voice interview simulation + AI feedback",
          "Precise ATS score + concrete suggestions",
        ],
      },
    },
    solutions: {
      title: "Everything you need to land the job",
      subtitle: "One platform, all the tools. Each solution works together to maximize your chances.",
      interview: {
        title: "Interview Prep",
        description: "Personalized questions based on your resume and the job. General, tech, consulting, finance.",
        cta: "Practice now",
      },
      ats: {
        title: "ATS Resume Analysis",
        description: "Compatibility score, missing keywords, before/after suggestions by industry.",
        cta: "Analyze my resume",
      },
      coverLetter: {
        title: "AI Cover Letter",
        description: "Generate a personalized cover letter in 30 seconds from your resume and the job posting.",
        cta: "Generate a letter",
        badge: "New",
      },
      resume: {
        title: "Resume Builder",
        description: "Create an ATS-optimized resume with pro templates and AI suggestions.",
        cta: "Build my resume",
      },
      linkedin: {
        title: "LinkedIn AI",
        description: "Analyze and optimize your LinkedIn profile: headline, summary, experience.",
        cta: "Optimize",
        badge: "New",
      },
    },
    howItWorks: {
      title: "3 steps to land your job",
      subtitle: "From resume upload to signing the contract.",
      step1: {
        title: "Import your resume",
        description: "Upload your PDF. Velyq extracts your experience automatically.",
      },
      step2: {
        title: "Choose a tool",
        description: "Interview, ATS, cover letter, LinkedIn — your data syncs everywhere.",
      },
      step3: {
        title: "Land the job",
        description: "AI does the work. You focus on what matters: applying.",
      },
    },
    whyVelyq: {
      title: "Why Velyq?",
      subtitle: "What other platforms don't do.",
      reason1: {
        title: "Built FR-first",
        description: "Tech, consulting, finance modes tailored to French companies and work culture.",
      },
      reason2: {
        title: "Connected ecosystem",
        description: "Your resume feeds your interviews. Your letter reuses your skills. Everything links — no copy-paste.",
      },
      reason3: {
        title: "Affordable voice AI",
        description: "Real-time voice interviews at €25/month. Competitors charge €148+ for the same tech.",
      },
      reason4: {
        title: "Generous freemium",
        description: "All tools on free tier. No credit card. Upgrade whenever you want.",
      },
    },
    features: {
      title: "Built to get you hired",
      interview: {
        title: "Personalized questions",
        description:
          "AI generates questions tailored to your resume and target position. Behavioral, technical or motivation.",
      },
      feedback: {
        title: "Detailed feedback",
        description:
          "After each answer, get an evaluation with concrete tips to improve.",
      },
      cv: {
        title: "Resume analysis",
        description:
          "Upload your resume (PDF) and Velyq automatically extracts your experiences to personalize questions.",
      },
      progress: {
        title: "Progress tracking",
        description: "Visualize your scores over time and identify areas to work on.",
      },
      star: {
        title: "STAR method",
        description:
          "Learn to structure your answers with the Situation-Task-Action-Result method.",
      },
      pitch: {
        title: "Pitch training",
        description:
          "Perfect your \"Tell me about yourself\" presentation with personalized feedback.",
      },
    },
    faq: {
      title: "Frequently asked questions",
      q1: {
        question: "Is it really free?",
        answer: "Yes, Velyq offers a free plan with access to all tools: interviews, ATS analysis, cover letters and more. The Pro plan unlocks unlimited usage.",
      },
      q2: {
        question: "How does the AI work?",
        answer: "Velyq runs on several state-of-the-art AI models to generate relevant questions, analyze your resume and produce personalized content. The best-fitting model is picked per task.",
      },
      q3: {
        question: "Is my data secure?",
        answer: "Yes. Your resume and data are stored securely. We never share your data with third parties.",
      },
      q4: {
        question: "Can I use Velyq in French?",
        answer: "Yes! All tools work in French and English. You can switch languages at any time.",
      },
      q5: {
        question: "What's included in Premium?",
        answer: "Premium unlocks unlimited usage of every tool: interviews (classic and voice Live), ATS analyses, cover letters, LinkedIn rewrite, Resume Builder and sector modes (tech, consulting, finance).",
      },
    },
    cta: {
      title: "Ready to land your next job?",
      subtitle: "Create your account in 30 seconds. Access all tools for free, no credit card.",
      button: "Start free",
      reassurance: "Free · No credit card · Under 30 seconds",
    },
    pricing: {
      title: "Pricing",
      free: {
        name: "Free",
        price: "€0",
        description: "All tools included",
        features: [
          "3 interviews / month",
          "1 ATS analysis / month",
          "1 cover letter / month",
          "LinkedIn analysis",
          "No credit card",
        ],
      },
      premium: {
        name: "Premium",
        price: "€9.99/month",
        description: "Unlimited, everything included",
        features: [
          "Unlimited interviews (classic + voice Live)",
          "Unlimited ATS analyses",
          "Unlimited cover letters",
          "LinkedIn: AI rewrite",
          "Full Resume Builder · PDF export",
          "Tech, consulting, finance modes",
        ],
      },
    },
  },

  // Dashboard
  dashboard: {
    welcome: "Welcome",
    welcomeBack: "Welcome back",
    stats: {
      interviews: "Interviews",
      avgScore: "Avg. score",
      progress: "Progress",
      streak: "Streak",
    },
    quickActions: {
      title: "Quick actions",
      newInterview: "New interview",
      uploadCV: "Upload resume",
      viewHistory: "View history",
    },
    recentActivity: {
      title: "Recent activity",
      noActivity: "No recent activity",
    },
  },

  // Profile
  profile: {
    title: "My profile",
    subtitle: "Manage your personal information",
    tabs: {
      personal: "Personal info",
      academic: "Academic background",
      career: "Career goals",
      preferences: "Preferences",
    },
    personal: {
      fullName: "Full name",
      email: "Email",
      avatar: "Profile picture",
      changeAvatar: "Change picture",
    },
    academic: {
      school: "School / University",
      fieldOfStudy: "Field of study",
      graduationYear: "Graduation year",
      experienceLevel: "Experience level",
      levels: {
        student: "Student",
        junior: "Junior (0-2 years)",
        mid: "Mid-level (2-5 years)",
        senior: "Senior (5+ years)",
      },
    },
    career: {
      targetRoles: "Target roles",
      targetRolesPlaceholder: "e.g. Developer, Data Analyst, Project Manager",
      targetSectors: "Target sectors",
      targetSectorsPlaceholder: "e.g. Tech, Finance, Consulting",
    },
    preferences: {
      uiLanguage: "Display language",
      interviewLanguage: "Interview language",
      languages: {
        fr: "Français",
        en: "English",
      },
    },
    saveSuccess: "Profile updated successfully",
    saveError: "Error updating profile",
  },

  // Groups
  groups: {
    title: "Groups",
    myGroups: "My groups",
    createGroup: "Create group",
    joinGroup: "Join group",
    members: "members",
    types: {
      school: "School",
      company: "Company",
      other: "Other",
    },
    roles: {
      owner: "Owner",
      admin: "Admin",
      member: "Member",
    },
  },

  // Interviews
  interviews: {
    title: "Interviews",
    new: "New interview",
    history: "History",
    types: {
      behavioral: "Behavioral",
      technical: "Technical",
      motivation: "Motivation",
      mixed: "Mixed",
    },
    difficulty: {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
    },
    status: {
      created: "Created",
      in_progress: "In progress",
      completed: "Completed",
      abandoned: "Abandoned",
    },
  },

  // ATS Analysis
  ats: {
    title: "ATS Analysis",
    subtitle: "Check your CV compatibility with a job offer",
    analyzing: "Analyzing...",
    analyzingDescription: "Our AI is comparing your CV with the job criteria",
    analyzeCompatibility: "Analyze compatibility",
    score: "ATS Score",
    summary: "Summary",
    // Results page
    results: {
      title: "ATS Analysis Results",
      backToResume: "Back to resume",
      reanalyze: "Re-analyze",
      exportPdf: "Export PDF",
      editResume: "Edit resume",
    },
    // Section scores
    sections: {
      title: "Section scores",
      experience: "Experience",
      skills: "Skills",
      education: "Education",
      format: "Format",
    },
    // Keywords
    keywords: {
      matched: "Matched keywords",
      missing: "Missing keywords",
      foundIn: "Found in",
      addTo: "Add to",
    },
    // Tips & Suggestions
    tips: {
      title: "Improvement tips",
      priority: {
        high: "High priority",
        medium: "Medium priority",
        low: "Low priority",
      },
      categories: {
        skills: "Skills",
        experience: "Experience",
        education: "Education",
        format: "Format",
        summary: "Summary",
        general: "General",
      },
      before: "Before",
      after: "After",
    },
    // Strengths & Issues
    strengths: "Strengths",
    formatIssues: "Format issues",
    // Job offer
    jobOffer: {
      title: "Job offer",
      existingOffer: "Existing offer",
      newOffer: "New offer",
      noSavedOffers: "No saved job offers",
      pasteText: "Paste text",
      manualInput: "Manual input",
      urlInput: "URL",
      comingSoon: "Coming soon",
      parseWithAI: "Parse with AI",
      parsing: "Parsing...",
      parsed: "Offer parsed",
      startOver: "Start over",
      useThisOffer: "Use this offer",
      jobTitle: "Job title",
      company: "Company",
      location: "Location",
      contractType: "Contract type",
      description: "Job description",
      requirements: "Requirements",
      keySkills: "Key skills",
      addSkill: "Add a skill",
      requiredSkills: "Required skills",
      niceToHave: "Nice to have",
      experienceLevel: "Experience",
    },
    // Premium
    premium: {
      unlockTitle: "Unlock with Premium",
      unlockDescription: "Access the full analysis",
      hiddenItems: "hidden items",
      limitReached: "Limit reached",
      freeRemaining: "free analysis remaining",
      unlimitedAnalyses: "Premium = unlimited analyses + full results",
      goPremium: "Go Premium to analyze",
    },
    // CV Viewer
    cvViewer: {
      viewMode: "View",
      editMode: "Edit",
      showHighlights: "Show ATS highlights",
      hideHighlights: "Hide ATS highlights",
    },
    // Job types
    jobTypes: {
      cdi: "Full-time",
      cdd: "Fixed-term",
      internship: "Internship",
      apprenticeship: "Apprenticeship",
      freelance: "Freelance",
      other: "Other",
    },
  },

  // Footer
  footer: {
    rights: "All rights reserved",
    privacy: "Privacy",
    terms: "Terms of service",
    contact: "Contact",
  },

  // Blog
  blog: {
    pageTitle: "Velyq Blog — Career, interview and resume guides",
    heroTitle: "Blog",
    heroSubtitle:
      "Advice, strategies and techniques to nail your interviews and land the right role.",
    featuredSection: "Featured articles",
    allSection: "All articles",
    empty: "New articles coming soon…",
    noResults: "No article matches your search.",
    resetFilters: "Reset",
    searchPlaceholder: "Search an article…",
    allCategories: "All categories",
    readArrow: "Read",
    readTimeSuffix: "min",
    ctaTitle: "Ready to put this into practice?",
    ctaSubtitle: "Train with our AI interview simulator.",
    ctaButton: "Get started for free",
    article: {
      backToBlog: "Back to blog",
      readTime: "min read",
      relatedTitle: "Related articles",
      tocLabel: "On this page",
      ctaTitle: "Ready to put this into practice?",
      ctaSubtitle:
        "Train with our AI interview simulator and get personalized feedback.",
      ctaButton: "Get started for free",
    },
  },

  // Training Hub (cold call simulator)
  // V1 ships FR-only — these EN values are FR placeholders, real translation deferred.
  training: {
    nav: "Entraînement",
    title: "Entraînement",
    subtitle: "Simulateurs vocaux IA pour les commerciaux français",
    quota: {
      usedFree: "Free · {used}/{limit} ce mois",
      unlimited: "Premium · illimité",
      limitReached: "Limite atteinte",
      tooltipFree:
        "3 sessions gratuites par mois. Passe en Premium pour des cold calls illimités.",
      tooltipPremium: "Plan Premium — sessions illimitées.",
    },
    categories: {
      cold_call: "Cold Call SDR",
    },
    difficulty: {
      easy: "Facile",
      medium: "Moyen",
      hard: "Difficile",
    },
    status: {
      created: "Créée",
      in_progress: "En cours",
      completed: "Terminée",
      abandoned: "Abandonnée",
      scored: "Notée",
    },
    hub: {
      lastSessions: "Tes dernières sessions",
      seeAll: "Tout voir",
      noSessionsYet:
        "Aucune session pour l'instant — choisis un persona ci-dessus pour commencer.",
      personasEmpty:
        "Aucune persona disponible. Vérifie que le backend est bien initialisé.",
    },
    persona: {
      backToHub: "Retour à l'entraînement",
      traitsTitle: "Traits",
      contextTitle: "Contexte",
      objectionsTitle: "Objections probables",
      scenariosTitle: "Scénarios disponibles",
      scenariosCount: "{n} scénario(s) disponible(s)",
      durationMinutes: "{n} min",
      notFound: "Persona introuvable",
    },
    scenario: {
      backToPersona: "Retour à la persona",
      personaTitle: "Persona",
      contextTitle: "Contexte de l'appel",
      objectionsTitle: "Objections attendues",
      scoringTitle: "Tu seras scoré sur",
      startCta: "Démarrer l'appel · {minutes} min",
      starting: "Création de la session…",
      notFound: "Scénario introuvable",
      durationMinutes: "{n} min",
      customPitchTitle: "Pitch your product (optional)",
      customPitchPlaceholder: "Describe your product in 1-3 sentences for more realistic objections…",
      customPitchPlaceholderFreeLocked: "Pitch your real product in Premium",
      customPitchSavedHint: "Pre-filled from your settings. Editing here only changes this session.",
    },
    live: {
      preparing: "Preparing the call…",
      micNotice:
        "Your mic will be activated. Speak naturally — the AI is listening and replies in real time.",
      startCallCta: "Start call",
      hangupCta: "Hang up",
      mute: "Mute mic",
      unmute: "Unmute mic",
      ending: "Saving…",
      transcriptTitle: "Live transcript",
      transcriptEmpty: "The conversation will appear here as soon as you speak…",
      messages: "messages",
      you: "You",
      agent: "AI",
      errorTitle: "Connection failed",
      errorBody: "We couldn't reach the AI agent. Try again in a moment.",
      toastConnectionError: "AI agent connection interrupted",
      toastStartError: "Couldn't start the call — check your microphone.",
      toastEndError: "Session ended but we couldn't save it. We'll retry.",
    },
    results: {
      notFound: "Session not found",
      backToSessions: "Back to my sessions",
      backToCatalog: "Back to catalog",
      viewAllSessions: "View my sessions",

      // Loading + states
      scoringInProgress: "Analyzing…",
      scoringHint:
        "The coach is analyzing your call against Sandler / Challenger / SPIN / LAER frameworks. This takes 5 to 15 seconds.",
      scoringFailedTitle: "Analysis failed",
      scoringFailedBody:
        "We couldn't finalize the analysis. Please retry in a moment.",
      retryAnalysis: "Retry analysis",

      // Verdicts
      verdictProduction: "Production-quality call",
      verdictBookable: "Meeting bookable — a few axes to fix",
      verdictReplay: "Replay needed — foundations to rebuild",
      verdictShortCall: "Call too short for reliable scoring",

      // Tabs
      tabView: "Overview",
      tabTranscript: "Transcript",
      tabCoaching: "Coaching",

      // Vue tab
      breakdownTitle: "Breakdown",
      top3Title: "Top 3 priorities for next session",
      voiceMetricsTitle: "Voice metrics",
      voiceTalkRatio: "Talk ratio",
      voicePace: "Pace",
      voiceFillers: "Fillers",
      estimatedBadge: "estimated",

      // Criteria
      criterion_opener: "Opener & Permission",
      criterion_pertinence: "Relevance & Personalization",
      criterion_discovery: "Discovery & Listening",
      criterion_objection: "Objection handling",
      criterion_closing: "Closing & CTA",

      // Coaching cards
      citationLabel: "Quote",
      adviceLabel: "Advice",
      exampleLabel: "Example",
      viewInTranscript: "View in transcript",

      // Transcript labels
      you: "You",
      agent: "AI",
    },
    sessions: {
      title: "Mes sessions d'entraînement",
      subtitle: "Toutes tes sessions de cold call, du plus récent au plus ancien.",
      emptyState:
        "Aucune session pour l'instant. Lance ton premier cold call depuis le catalogue.",
      goToCatalog: "Aller au catalogue",
      colDate: "Date",
      colScenario: "Scénario",
      colStatus: "Statut",
      colDuration: "Durée",
      colScore: "Score",
      durationDash: "—",
      scoreDash: "—",
      previous: "Précédent",
      next: "Suivant",
    },
    upgrade: {
      banner: "Tu as atteint la limite Free de 3 sessions ce mois.",
      bannerCta: "Passer en Premium",
      toastQuotaReached:
        "Tu as utilisé tes 3 sessions gratuites du mois. Passe en Premium pour continuer.",
      toastError: "Impossible de créer la session. Réessaie dans un instant.",
    },
    paywall: {
      quotaTitle: "You've used your 3 free sessions this month.",
      quotaBody: "Keep practicing without limits with Premium. Unlimited sessions, custom pitch, audio replay, PDF export — €9.99/month.",
      quotaPrimary: "Upgrade to Premium",
      quotaSecondary: "Later",
      personaHardTitle: "{name} is a Premium persona.",
      personaHardBody: "Hard personas (annoyed CIO, formal CFO) are the toughest — Premium only.",
      personaHardPrimary: "Unlock Premium",
      personaHardSecondary: "See Free personas",
      customPitchTitle: "Pitch your real product in Premium.",
      customPitchBody: "Paste your own pitch and the AI prospect adapts to your real product — not a fictional one.",
      customPitchPrimary: "Upgrade to Premium",
      customPitchSecondary: "Later",
    },
  },

  // Settings
  settings: {
    customPitch: {
      title: "My product",
      subtitle: "Describe your product for more realistic training sessions.",
      placeholder: "Ex: we sell a SaaS expense management tool for SMBs of 50-500 employees…",
      saveCta: "Save",
      saving: "Saving…",
      saved: "Saved.",
      saveError: "Couldn't save. Try again.",
      lockedHint: "Available with Premium.",
      lockedCta: "Unlock Premium",
      maxLengthHint: "{count}/2000 characters",
    },
  },
} as const;
